import {
  Children,
  FC,
  PropsWithChildren,
  ReactNode,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react";

import SubscriptionModal from "web/Layout/SubscriptionModal";
import __ from "web/Layout/Translations";

import { ModalActionTypes } from "web/hooks/useModal/useModalTypes";

import { useAppContext } from "web/context/app";

import classes from "./subscriptionSummary.scss";

interface ISubscriptionSummaryProps {
  name: string;
  quantity: number;
  additionalPersonInfo?: ReactNode;
  showAdditionalPersonInfo?: boolean;
}

const getTextContent = (node: ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getTextContent(node.props.children);
  }

  return Children.toArray(node).map(getTextContent).join("");
};

const SubscriptionSummary: FC<PropsWithChildren<ISubscriptionSummaryProps>> = ({
  children,
  name,
  quantity,
  additionalPersonInfo,
  showAdditionalPersonInfo,
}) => {
  const { modal } = useAppContext();
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const additionalPersonInfoText = getTextContent(additionalPersonInfo);
  const shouldShowAdditionalPersonInfo = !!(
    showAdditionalPersonInfo && additionalPersonInfoText
  );

  useEffect(() => {
    if (!shouldShowAdditionalPersonInfo) return undefined;

    const checkTruncation = () => {
      const element = textRef.current;
      if (element) setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [shouldShowAdditionalPersonInfo, additionalPersonInfoText]);

  const showAdditionalPersonInfoModal = () => {
    modal.dispatch({
      type: ModalActionTypes.ADD_MODAL,
      modal: (
        <SubscriptionModal
          title={__("Ważne informacje dotyczące abonamentu")}
          description={
            <>
              <h3>{name}</h3>
              {additionalPersonInfo}
            </>
          }
        />
      ),
    });
  };

  return (
    <article className={classes.root}>
      <div className={classes.header}>
        <div className={classes.name}>
          <span>{name}</span>
        </div>
        <div className={classes.details}>
          <div
            className={classes.item}
            data-t1="numberOfPeople"
            data-t2={quantity}
          >{`${quantity} ${__("os.")}`}</div>
        </div>
      </div>
      {shouldShowAdditionalPersonInfo && (
        <div className={classes.additionalPersonInfo}>
          <span ref={textRef} className={classes.additionalPersonInfoText}>
            {additionalPersonInfoText}
          </span>
          {isTruncated && (
            <button
              type="button"
              className={classes.more}
              onClick={showAdditionalPersonInfoModal}
            >
              {__("czytaj więcej")}
            </button>
          )}
        </div>
      )}
      {children}
    </article>
  );
};

export default SubscriptionSummary;
